import * as React from "react";

import { ActionSource, asConditionVariable, isUploadFileResponseError } from "common/components/types";
import { loadedDefinedMap, asString, asBoolean } from "common/components/component-helpers";
import type { GlideFunctionalRenderer } from "common/components/uis";
import { useForceValueProducers } from "hooks/use-value-producers";
import { useForce } from "hooks/use-force";
import { useNetworkStatus } from "hooks/use-network-status";
import { useOnClickFromAction } from "hooks/use-on-click-from-action";
import Signature from "../ui/smart/signature/signature";
import { dontAwait } from "@glideapps/ts-necessities";

export const SignaturePadMaterialRenderer: GlideFunctionalRenderer = p => {
    const { env, getValueProducers, ctx } = p;
    const [force] = useForce();
    const [
        [message, , onChangeValue, shouldWriteFlag, , , shouldWriteCV, isWritingCV],
        [, valueProducer, , , validFlag, hasPathsVP],
    ] = useForceValueProducers(getValueProducers, force, ctx);
    const onLine = useNetworkStatus();
    const { onClick: onChangeCB } = useOnClickFromAction(onChangeValue, ActionSource.Signature, ctx, env);

    const setValid = React.useCallback(
        (isValid: boolean) => {
            if (validFlag === undefined) return;
            validFlag.set(isValid);
        },
        [validFlag]
    );

    const onChange = React.useCallback(
        (newValue: string): void => {
            if (valueProducer === undefined) return;
            valueProducer.set(newValue);

            if (onChangeCB !== undefined) {
                dontAwait(onChangeCB(undefined));
            }
        },
        [valueProducer, onChangeCB]
    );

    const uploadImageCB = React.useCallback(
        async (file: File) => {
            if (onChange === undefined) return;
            setValid(false);

            const cv = loadedDefinedMap(isWritingCV, asConditionVariable);
            if (cv !== undefined) {
                cv.notifyAll();
            }

            // FIXME: Support progress reporting
            // FIXME: Handle errors
            // FIXME: Suppor cancellation
            const session = env.appEnvironment.uploadAppFile(file, "signature", undefined);
            const response = await session.attempt();

            if (!isUploadFileResponseError(response)) {
                onChange(response.path);
            }

            setValid(true);
        },
        [env.appEnvironment, isWritingCV, onChange, setValid]
    );

    // This isn't an action, but an upload operation would require the user to be online.  So if we know they're
    // offline, let's not allow them to upload at all.
    const uploadImage = onLine ? uploadImageCB : undefined;

    const onHasPathsChanged = React.useCallback(
        (newVal: boolean) => {
            hasPathsVP?.set(newVal);
        },
        [hasPathsVP]
    );

    const messageValue = loadedDefinedMap(message, asString);
    if (messageValue === undefined) return null;

    return (
        <Signature
            renderEnvironment={env}
            shouldWriteFlag={loadedDefinedMap(shouldWriteFlag, asBoolean)}
            shouldWriteCV={loadedDefinedMap(shouldWriteCV, asConditionVariable)}
            onSetFile={uploadImage}
            message={messageValue}
            smoothing={8}
            onHasPathsChanged={onHasPathsChanged}
        />
    );
};
